/**
 * Authentication Module
 *
 * @module auth
 * @category Authentication
 *
 * Handles user authentication including:
 * - Registration
 * - Login (email/username/phone + password)
 * - Verification code login
 * - OAuth social login
 * - Logout and token refresh
 */

import {
  detectMiniProgramPlatform,
  getMiniProgramLogin,
} from "@amaster.ai/http-client";
import type { HttpClient, ClientResult } from "@amaster.ai/http-client";
import type {
  RegisterParams,
  LoginParams,
  LoginResponse,
  LoginType,
  CodeLoginParams,
  CodeLoginType,
  SendCodeParams,
  SuccessResponse,
  CaptchaResponse,
  OAuthProvider,
  RefreshTokenResponse,
  User,
  MiniGetPhoneParams,
  MiniLoginParams,
  MiniProgramLoginParams,
  MiniProgramPhoneParams,
  MiniProgramPlatform,
  MiniProgramPhoneResponse,
} from "../types";
import { STORAGE_KEYS } from "../storage";

/**
 * Auto-detect login type based on provided parameters
 */
function inferLoginType(params: LoginParams): LoginType | undefined {
  if (params.email) return "email";
  if (params.username) return "username";
  if (params.phone) return "phone";
  return undefined;
}

/**
 * Auto-detect code login type based on provided parameters
 */
function inferCodeLoginType(params: CodeLoginParams): CodeLoginType | undefined {
  if (params.email) return "email";
  if (params.phone) return "phone";
  return undefined;
}

function resolveMiniProgramPlatform(platform?: MiniProgramPlatform): MiniProgramPlatform {
  if (platform) {
    return platform;
  }

  return detectMiniProgramPlatform() ?? "wechat";
}

function normalizeMiniProgramParams(
  input: string | MiniProgramLoginParams | MiniProgramPhoneParams
): { code: string; platform: MiniProgramPlatform } {
  if (typeof input === "string") {
    return {
      code: input,
      platform: resolveMiniProgramPlatform(),
    };
  }

  return {
    code: input.code,
    platform: resolveMiniProgramPlatform(input.platform),
  };
}

function getMiniProgramAuthEndpoint(
  action: "login" | "phone",
  platform: MiniProgramPlatform
): string {
  if (platform === "douyin") {
    return `/api/auth/miniprogram/douyin/${action}`;
  }

  return `/api/auth/miniprogram/${action}`;
}

async function requestMiniProgramLoginCode(
  platform?: MiniProgramPlatform
): Promise<{ code: string; platform: MiniProgramPlatform }> {
  const resolvedPlatform = resolveMiniProgramPlatform(platform);
  const login = getMiniProgramLogin(resolvedPlatform);

  const callLogin = async (
    login: (options?: {
      success?: (result: { code?: string }) => void;
      fail?: (error: unknown) => void;
    }) => Promise<{ code?: string }> | { code?: string } | void
  ): Promise<string> =>
    await new Promise((resolve, reject) => {
      let settled = false;

      const resolveCode = (result: { code?: string } | undefined) => {
        if (settled) {
          return;
        }
        settled = true;

        if (typeof result?.code === "string" && result.code) {
          resolve(result.code);
          return;
        }

        reject(new Error("Mini Program login failed: missing code"));
      };

      const rejectWithError = (error: unknown) => {
        if (settled) {
          return;
        }
        settled = true;

        reject(
          new Error(
            `Mini Program login failed: ${error instanceof Error ? error.message : String(error)}`
          )
        );
      };

      try {
        const result = login({
          success: resolveCode,
          fail: rejectWithError,
        });

        if (result && typeof (result as PromiseLike<{ code?: string }>).then === "function") {
          void (result as Promise<{ code?: string }>).then(resolveCode, rejectWithError);
          return;
        }

        if (result && typeof result === "object" && "code" in result) {
          resolveCode(result as { code?: string });
        }
      } catch (error) {
        rejectWithError(error);
      }
    });

  if (typeof login !== "function") {
    throw new Error(`Mini Program login API is not available for platform '${resolvedPlatform}'`);
  }

  return {
    code: await callLogin(login),
    platform: resolvedPlatform,
  };
}

function resolveMiniGetPhonePayload(
  input: MiniGetPhoneParams
): { code: string; platform: MiniProgramPlatform } | null {
  if (typeof input === "string") {
    return normalizeMiniProgramParams(input);
  }

  const eventDetail =
    "detail" in input && input.detail && typeof input.detail === "object"
      ? input.detail
      : null;
  const eventCode =
    typeof eventDetail?.code === "string" && eventDetail.code
      ? eventDetail.code
      : typeof input.code === "string" && input.code
        ? input.code
        : undefined;

  if (!eventCode) {
    return null;
  }

  return {
    code: eventCode,
    platform: resolveMiniProgramPlatform(input.platform),
  };
}

function getOAuthCallbackAccessToken(hash: string): string | null {
  const params = new URLSearchParams(hash);
  return params.get("access_token") || params.get("accessToken");
}

function getOAuthCallbackUser(hash: string): string | null {
  const params = new URLSearchParams(hash);
  return params.get("user");
}

function getSafeRedirectTarget(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const redirect = new URLSearchParams(window.location.search).get("redirect");
    if (!redirect) {
      return null;
    }

    const resolvedUrl = new URL(redirect, window.location.origin);
    if (resolvedUrl.origin !== window.location.origin) {
      console.warn("[AuthClient] Ignored cross-origin redirect target:", redirect);
      return null;
    }

    return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
  } catch (error) {
    console.warn("[AuthClient] Failed to resolve redirect target:", error);
    return null;
  }
}

export const browserNavigation = {
  replace(url: string): void {
    window.location.replace(url);
  },
};

interface RedirectHandlingResult {
  handled: boolean;
  target?: string;
}

function withRedirectHandling(
  data: LoginResponse,
  redirectHandling: RedirectHandlingResult
): LoginResponse {
  if (!redirectHandling.handled) {
    return data;
  }

  return {
    ...data,
    redirectHandled: true,
    redirectTarget: redirectHandling.target,
  };
}

function maybeRedirectAfterLogin(autoRedirectAfterLogin: boolean): RedirectHandlingResult {
  if (!autoRedirectAfterLogin || typeof window === "undefined" || window.opener) {
    return { handled: false };
  }

  const redirectTarget = getSafeRedirectTarget();
  if (redirectTarget) {
    browserNavigation.replace(redirectTarget);
    return { handled: true, target: redirectTarget };
  }

  return { handled: false };
}

export interface AuthModuleDeps {
  http: HttpClient;
  onLoginSuccess: (user: User, accessToken: string) => void;
  onLogout: () => void;
  autoRedirectAfterLogin: boolean;
  storage: { 
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };
  clearAuth: () => void;
  onRefreshFailure?: (result: ClientResult<RefreshTokenResponse>) => void;
}

export function createAuthModule(deps: AuthModuleDeps) {
  const {
    http,
    onLoginSuccess,
    onLogout,
    autoRedirectAfterLogin,
    storage,
    clearAuth,
    onRefreshFailure,
  } = deps;

  return {
    /**
     * Register a new user account
     *
     * @category Authentication
     * @example
     * ```typescript
     * const result = await auth.register({
     *   email: "user@example.com",
     *   password: "Password@123",
     *   displayName: "John Doe",
     * });
     * ```
     */
    async register(params: RegisterParams): Promise<ClientResult<LoginResponse>> {
      const result = await http.request<LoginResponse>({
        url: "/api/auth/register",
        method: "post",
        headers: { "Content-Type": "application/json" },
        data: params,
      });

      if (result.data?.user && result.data?.accessToken) {
        onLoginSuccess(result.data.user, result.data.accessToken);
        const redirectHandling = maybeRedirectAfterLogin(autoRedirectAfterLogin);
        result.data = withRedirectHandling(result.data, redirectHandling);
      }

      return result;
    },

    /**
     * Login with username/email/phone and password
     *
     * @category Authentication
     * @example
     * ```typescript
     * // Auto-detect loginType from username
     * await auth.login({
     *   username: "john_doe",
     *   password: "Password@123",
     * });
     *
     * // Auto-detect loginType from email
     * await auth.login({
     *   email: "user@example.com",
     *   password: "Password@123",
     * });
     * ```
     */
    async login(params: LoginParams): Promise<ClientResult<LoginResponse>> {
      // Auto-detect loginType if not provided
      const loginType = params.loginType || inferLoginType(params);

      if (!loginType) {
        return {
          data: null,
          error: {
            message: "Unable to determine login type. Please provide email, username, or phone.",
            status: 400,
          },
          status: 400,
        };
      }

      // Prepare request data with explicit loginType
      const requestData = {
        ...params,
        loginType,
      };

      const result = await http.request<LoginResponse>({
        url: "/api/auth/login",
        method: "post",
        headers: { "Content-Type": "application/json" },
        data: requestData,
      });

      if (result.data?.user && result.data?.accessToken) {
        onLoginSuccess(result.data.user, result.data.accessToken);
        const redirectHandling = maybeRedirectAfterLogin(autoRedirectAfterLogin);
        result.data = withRedirectHandling(result.data, redirectHandling);
      }

      return result;
    },

    /**
     * Login with verification code
     *
     * @category Authentication
     * @example
     * ```typescript
     * // Auto-detect loginType from email
     * await auth.loginWithCode({
     *   email: "user@example.com",
     *   code: "123456",
     * });
     *
     * // Auto-detect loginType from phone
     * await auth.loginWithCode({
     *   phone: "13800138000",
     *   code: "123456",
     * });
     * ```
     */
    async loginWithCode(params: CodeLoginParams): Promise<ClientResult<LoginResponse>> {
      // Auto-detect loginType if not provided
      const loginType = params.loginType || inferCodeLoginType(params);

      if (!loginType) {
        return {
          data: null,
          error: {
            message: "Unable to determine login type. Please provide email or phone.",
            status: 400,
          },
          status: 400,
        };
      }

      // Prepare request data with explicit loginType
      const requestData = {
        ...params,
        loginType,
      };

      const result = await http.request<LoginResponse>({
        url: "/api/auth/login-with-code",
        method: "post",
        headers: { "Content-Type": "application/json" },
        data: requestData,
      });

      if (result.data?.user && result.data?.accessToken) {
        onLoginSuccess(result.data.user, result.data.accessToken);
        const redirectHandling = maybeRedirectAfterLogin(autoRedirectAfterLogin);
        result.data = withRedirectHandling(result.data, redirectHandling);
      }

      return result;
    },

    /**
     * Send verification code to email or phone
     *
     * @category Authentication
     * @example
     * ```typescript
     * await auth.sendCode({
     *   type: "email",
     *   email: "user@example.com",
     * });
     * ```
     */
    async sendCode(params: SendCodeParams): Promise<ClientResult<SuccessResponse>> {
      return http.request<SuccessResponse>({
        url: "/api/auth/send-code",
        method: "post",
        headers: { "Content-Type": "application/json" },
        data: params,
      });
    },

    /**
     * Get captcha image
     *
     * @category Authentication
     */
    async getCaptcha(): Promise<ClientResult<CaptchaResponse>> {
      return http.request<CaptchaResponse>({
        url: "/api/auth/captcha",
        method: "get",
      });
    },

    /**
     * Redirect to OAuth provider for authentication
     *
     * @category Authentication
     * @example
     * ```typescript
     * auth.loginWithOAuth("google");
     * ```
     */
    loginWithOAuth(provider: OAuthProvider, redirectUrl?: string): void {
      if (typeof window === "undefined") {
        console.error("[AuthClient] OAuth login is only available in browser environment");
        return;
      }

      const url = redirectUrl
        ? `/api/auth/oauth/${provider}?redirect_url=${encodeURIComponent(redirectUrl)}`
        : `/api/auth/oauth/${provider}`;

      window.location.href = url;
    },

    /**
     * Handle OAuth callback
     *
     * @category Authentication
     */
    async handleOAuthCallback(): Promise<ClientResult<LoginResponse>> {
      if (typeof window === "undefined") {
        return {
          data: null,
          error: {
            message: "OAuth callback is only available in browser environment",
            status: 400,
          },
          status: 400,
        };
      }

      try {
        const hash = window.location.hash.substring(1);
        const accessToken = getOAuthCallbackAccessToken(hash);
        const userJson = getOAuthCallbackUser(hash);

        if (!accessToken) {
          return {
            data: null,
            error: { message: "OAuth callback failed: missing access token", status: 400 },
            status: 400,
          };
        }

        let user: User;

        // Smart adaptation: if user data is in hash, use it; otherwise fetch from API
        if (userJson) {
          // User data provided in hash (legacy behavior)
          user = JSON.parse(decodeURIComponent(userJson)) as User;
        } else {
          // User data not in hash, fetch from API
          // First save the token so the request can use it
          storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
          
          const meResult = await http.request<User>({
            url: "/api/auth/me",
            method: "get",
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          
          if (!meResult.data) {
            return {
              data: null,
              error: {
                message: "OAuth callback failed: unable to fetch user info",
                status: meResult.status || 500,
              },
              status: meResult.status || 500,
            };
          }
          user = meResult.data;
        }

        onLoginSuccess(user, accessToken);

        // Clear the hash from URL to avoid security issues and re-processing
        // Use replaceState to avoid adding to browser history
        if (window.history && window.history.replaceState) {
          const urlWithoutHash = window.location.pathname + window.location.search;
          window.history.replaceState(null, '', urlWithoutHash);
        } else {
          // Fallback for older browsers
          window.location.hash = '';
        }

        const redirectHandling = maybeRedirectAfterLogin(autoRedirectAfterLogin);

        return {
          data: withRedirectHandling({ user, accessToken }, redirectHandling),
          error: null,
          status: 200,
        };
      } catch (error) {
        return {
          data: null,
          error: {
            message: `OAuth callback failed: ${error instanceof Error ? error.message : String(error)}`,
            status: 400,
          },
          status: 400,
        };
      }
    },

    /**
     * Login with Mini Program code
     *
     * Lower-level API for submitting an already acquired mini-program login code.
     * For most app code, prefer `miniLogin()`.
     *
     * @deprecated Prefer `miniLogin()` unless you already have a resolved mini-program login code.
     *
     * @category Authentication
     */
    async loginWithMiniProgram(
      input: string | MiniProgramLoginParams
    ): Promise<ClientResult<LoginResponse>> {
      const { code, platform } = normalizeMiniProgramParams(input);
      const result = await http.request<LoginResponse>({
        url: getMiniProgramAuthEndpoint("login", platform),
        method: "post",
        headers: { "Content-Type": "application/json" },
        data: { code },
      });

      if (result.data?.user && result.data?.accessToken) {
        onLoginSuccess(result.data.user, result.data.accessToken);
        const redirectHandling = maybeRedirectAfterLogin(autoRedirectAfterLogin);
        result.data = withRedirectHandling(result.data, redirectHandling);
      }

      return result;
    },

    /**
     * Unified Mini Program login entry.
     *
     * Automatically calls Taro / WeChat `wx` / Douyin `tt` login APIs to fetch the temporary login code,
     * then submits it to the backend using the correct platform endpoint.
     *
     * @category Authentication
     * @example
     * ```typescript
     * const result = await auth.miniLogin();
     *
     * const douyinResult = await auth.miniLogin({
     *   platform: "douyin",
     * });
     * ```
     */
    async miniLogin(params: MiniLoginParams = {}): Promise<ClientResult<LoginResponse>> {
      try {
        const payload = await requestMiniProgramLoginCode(params.platform);
        return await this.loginWithMiniProgram(payload);
      } catch (error) {
        return {
          data: null,
          error: {
            message: error instanceof Error ? error.message : String(error),
            status: 400,
          },
          status: 400,
        };
      }
    },

    /**
     * Get Mini Program user phone number
     * Requires user authorization via getPhoneNumber button
     *
     * Lower-level API for submitting an already acquired phone code.
     * For most app code, prefer `miniGetPhone(...)`.
     *
     * @deprecated Prefer `miniGetPhone(...)` unless you already have a resolved phone authorization code.
     *
     * @category Authentication
     */
    async getMiniProgramPhoneNumber(
      input: string | MiniProgramPhoneParams
    ): Promise<ClientResult<MiniProgramPhoneResponse>> {
      const { code, platform } = normalizeMiniProgramParams(input);
      const token = storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      return http.request<MiniProgramPhoneResponse>({
        url: getMiniProgramAuthEndpoint("phone", platform),
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: { code },
      });
    },

    /**
     * Unified Mini Program phone binding entry.
     *
     * Accepts a raw code, a `{ code, platform }` object, or a Mini Program event object
     * such as the payload received from WeChat `wx`, Douyin `tt`, or Taro `getPhoneNumber`.
     *
     * @category Authentication
     * @example
     * ```typescript
     * const result = await auth.miniGetPhone(e);
     *
     * const douyinResult = await auth.miniGetPhone({
     *   code,
     *   platform: "douyin",
     * });
     * ```
     */
    async miniGetPhone(input: MiniGetPhoneParams): Promise<ClientResult<MiniProgramPhoneResponse>> {
      const payload = resolveMiniGetPhonePayload(input);

      if (!payload) {
        return {
          data: null,
          error: {
            message: "Unable to resolve mini-program phone code from input",
            status: 400,
          },
          status: 400,
        };
      }

      return this.getMiniProgramPhoneNumber(payload);
    },

    /**
     * Logout current user
     *
     * @category Authentication
     * @example
     * ```typescript
     * await auth.logout();
     * ```
     */
    async logout(): Promise<ClientResult<SuccessResponse>> {
      const token = storage.getItem("amaster_access_token");

      const result = await http.request<SuccessResponse>({
        url: "/api/auth/logout",
        method: "post",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      clearAuth();
      onLogout();
      return result;
    },

    /**
     * Refresh access token
     *
     * @category Authentication
     */
    async refreshToken(): Promise<ClientResult<RefreshTokenResponse>> {
      const result = await http.request<RefreshTokenResponse>({
        url: "/api/auth/refresh",
        method: "post",
      });

      // Save new token to storage if refresh succeeded
      if (result.data?.accessToken) {
        storage.setItem("amaster_access_token", result.data.accessToken);
      } else if (result.status === 401 || result.status === 403) {
        onRefreshFailure?.(result);
      }

      return result;
    },
  };
}

export type AuthModule = ReturnType<typeof createAuthModule>;
